import * as React from "react";
import { api, postJson } from "./api";
import { FragmentCard, type FragmentView } from "./FragmentCard";
import { AppLink, useNavigation } from "./router";
import { TrackSearch } from "./TrackSearch";
import type { TrackResult } from "./track-search";
import { useRoomSession, type AppUser } from "./room-session";

interface ProfilePayload {
  profile: {
    handle: string;
    displayName: string;
    avatarUrl: string | null;
    bio: string;
    libraryTitle: string;
    accent: string;
    isOwner: boolean;
  };
  liveRoom: null | {
    id: string;
    alias: string;
    title: string;
    listenerCount: number;
    track: null | { title: string; artist: string; artworkUrl: string | null };
  };
  featured: FragmentView[];
  featuredCount: number;
  libraryCount: number;
  wallCount: number;
  wall: FragmentView[];
  library: FragmentView[];
  nextCursors: { wall: string | null; library: string | null };
}

function SendComposer({ track, handle, onCancel, onSent }: {
  track: TrackResult;
  handle: string;
  onCancel: () => void;
  onSent: () => void;
}) {
  const [visibility, setVisibility] = React.useState<"public" | "private">("public");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const send = async () => {
    setBusy(true);
    setError("");
    try {
      await postJson("/api/fragments", {
        trackId: track.spotifyId,
        destination: { kind: visibility === "public" ? "profile_wall" : "profile_inbox", id: handle },
        visibility,
        sourceKind: "profile",
        note: note.trim() || null,
      });
      onSent();
    } catch (cause: any) {
      setError(cause?.message || "Could not send this Fragment");
    } finally { setBusy(false); }
  };
  return (
    <div className="profile-composer-selected">
      <div className="selected-track"><div className="track-art mini-art">{track.artworkUrl ? <img src={track.artworkUrl} alt="" width="52" height="52" /> : "♪"}</div><div><strong>{track.title}</strong><small>{track.artist} · {track.album}</small></div><button className="icon-button" onClick={onCancel}>×</button></div>
      <div className="segmented visibility-toggle"><button className={visibility === "public" ? "active" : ""} onClick={() => setVisibility("public")}>Post to Wall</button><button className={visibility === "private" ? "active" : ""} onClick={() => setVisibility("private")}>Send privately</button></div>
      <label><span>Add a note <small>{note.length}/280</small></span><textarea value={note} maxLength={280} onChange={(event) => setNote(event.target.value)} placeholder={`Tell @${handle} why this song found them…`} /></label>
      {error && <p className="field-error">{error}</p>}
      <button className="button primary full" onClick={() => void send()} disabled={busy}>{busy ? "Sending Fragment…" : visibility === "public" ? "Post to their Wall" : "Send to private Inbox"}</button>
    </div>
  );
}

export function ProfilePage({ handle, user, onRequireAuth }: {
  handle: string;
  user: AppUser | null;
  onRequireAuth: (pendingAction?: string) => void;
}) {
  const { navigate } = useNavigation();
  const roomSession = useRoomSession();
  const [payload, setPayload] = React.useState<ProfilePayload | null>(null);
  const [tab, setTab] = React.useState<"featured" | "wall" | "library" | "inbox">("wall");
  const [sort, setSort] = React.useState("recent");
  const [filter, setFilter] = React.useState("all");
  const [selectedTrack, setSelectedTrack] = React.useState<TrackResult | null>(null);
  const [inbox, setInbox] = React.useState<FragmentView[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState("");
  const pickedInitialTab = React.useRef(false);

  React.useEffect(() => {
    pickedInitialTab.current = false;
    setTab("wall");
    setSort("recent");
    setFilter("all");
  }, [handle]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ sort });
      if (tab === "wall") params.set("wallFilter", filter);
      if (tab === "library") params.set("libraryFilter", filter);
      const next = await api<ProfilePayload & { status: string }>(`/api/profiles/${encodeURIComponent(handle)}?${params}`);
      setPayload(next);
      if (!pickedInitialTab.current) {
        pickedInitialTab.current = true;
        if (next.featuredCount > 0) setTab("featured");
      }
      if (next.profile.isOwner) {
        void api<{ fragments: FragmentView[] }>("/api/me/fragments/inbox").then((data) => setInbox(data.fragments));
      }
    } catch (cause: any) {
      setError(cause?.message || "Profile not found");
    } finally { setLoading(false); }
  }, [filter, handle, sort, tab]);

  React.useEffect(() => { void load(); }, [load]);

  const loadMore = async () => {
    if (!payload || tab === "inbox" || tab === "featured") return;
    const cursor = payload.nextCursors[tab];
    if (!cursor) return;
    setLoadingMore(true);
    setError("");
    try {
      const params = new URLSearchParams({ sort, [`${tab}Cursor`]: cursor });
      if (tab === "wall") params.set("wallFilter", filter);
      if (tab === "library") params.set("libraryFilter", filter);
      const next = await api<ProfilePayload>(`/api/profiles/${encodeURIComponent(handle)}?${params}`);
      setPayload((current) => current ? {
        ...current,
        wall: tab === "wall" ? [...current.wall, ...next.wall] : current.wall,
        library: tab === "library" ? [...current.library, ...next.library] : current.library,
        nextCursors: { ...current.nextCursors, [tab]: next.nextCursors[tab] },
      } : next);
    } catch (cause: any) {
      setError(cause?.message || "Could not load more songs");
    } finally {
      setLoadingMore(false);
    }
  };

  const joinLiveRoom = async () => {
    if (!payload?.liveRoom) return;
    if (await roomSession.joinRoom(payload.liveRoom.alias)) navigate(`/rooms/${encodeURIComponent(payload.liveRoom.alias)}`);
  };

  if (loading && !payload) return <main className="profile-loading page-shell"><div className="profile-skeleton"><div /><div /><div /></div></main>;
  if (error && !payload) return <main className="not-found page-shell"><span>404</span><h1>{error}</h1><AppLink href="/">Back to the room dial</AppLink></main>;
  if (!payload) return null;
  const { profile, liveRoom } = payload;
  const fragments = tab === "featured" ? payload.featured : tab === "wall" ? payload.wall : tab === "library" ? payload.library : inbox;
  const sortOptions = tab === "library"
    ? [["recent", "Recently Saved"], ["artist", "Artist → Album → Track"], ["passed", "Most Passed Along"]]
    : [["recent", "Newest"], ["passed", "Most Passed Along"], ["sender", "Sender → Newest"]];
  const filterOptions = tab === "library"
    ? [["all", "All"], ["search", "Found in Search"], ["profile", "From Profiles"], ["room", "From Rooms"]]
    : [["all", "All"], ["not_saved", "Not in Library"], ["saved", "Added to Library"]];
  const nextCursor = tab === "inbox" || tab === "featured" ? null : payload.nextCursors[tab];

  return (
    <main className={`profile-page profile-accent-${profile.accent}`}>
      <div className="profile-stars" aria-hidden="true">✦　·　✧　　✦　·　✧　　✦</div>
      <div className="profile-canvas page-shell">
        <header className="profile-identity">
          <div className="profile-avatar-frame">{profile.avatarUrl ? <img src={profile.avatarUrl} alt="" width="156" height="156" /> : <span>{profile.displayName.slice(0, 1)}</span>}<i /></div>
          <div className="profile-title"><span className="eyebrow">JAMBAND PROFILE</span><h1>{profile.displayName}</h1><p>@{profile.handle}</p><blockquote>{profile.bio || "Songs I want to remember, and the people who sent them."}</blockquote></div>
          <div className="profile-stamp" aria-hidden="true"><span>VISITOR</span><b>RADIO</b><small>EST. {new Date().getFullYear()}</small></div>
        </header>

        <div className="profile-columns">
          <aside className="profile-left-rail">
            <section className={`live-profile-module ${liveRoom ? "is-live" : ""}`}>
              <header><span><i /> {liveRoom ? "LIVE NOW" : "OFF AIR"}</span></header>
              {liveRoom ? <><div className="live-track-art">{liveRoom.track?.artworkUrl ? <img src={liveRoom.track.artworkUrl} width="240" height="240" alt="" /> : <span>♪</span>}</div><h2>{liveRoom.title}</h2><p>{liveRoom.track ? `${liveRoom.track.title} · ${liveRoom.track.artist}` : "Waiting for a song"}</p><small>{liveRoom.listenerCount} listener{liveRoom.listenerCount === 1 ? "" : "s"}</small><button className="button signal full" onClick={() => void joinLiveRoom()}>Join room →</button></> : <><div className="off-air-graphic">◌</div><h2>Not in a public room</h2><p>Private and unlisted listening never appears here.</p></>}
            </section>
            <section className="profile-card profile-about"><span className="eyebrow">ABOUT THIS LIBRARY</span><h2>{profile.libraryTitle}</h2><p>{profile.bio || "A public shelf of songs worth keeping."}</p><div className="profile-stats"><span><b>{payload.featuredCount}</b> featured</span><span><b>{payload.libraryCount}</b> saved</span><span><b>{payload.wallCount}</b> wall posts</span></div></section>
          </aside>

          <div className="profile-main-column">
            {profile.isOwner && <section className="spotify-profile-studio profile-card"><div><span className="eyebrow">SPOTIFY PROFILE STUDIO</span><h2>{payload.featuredCount ? "Refresh your Featured rotation" : "Build your Featured 60"}</h2><p>Your Liked Songs and playlists become a private draft. You approve every public pick.</p></div><AppLink className="button spotify-button" href={`/u/${encodeURIComponent(profile.handle)}/spotify-import`}><i>●</i> {payload.featuredCount ? "Refresh from Spotify" : "Import from Spotify"}</AppLink></section>}
            {!profile.isOwner && <section className="recommend-panel profile-card"><header><div><span className="eyebrow">LEAVE A SONG FRAGMENT</span><h2>Send something to {profile.displayName}</h2></div><span className="tape-label">exact songs only</span></header>{selectedTrack ? <SendComposer track={selectedTrack} handle={profile.handle} onCancel={() => setSelectedTrack(null)} onSent={() => { setSelectedTrack(null); setTab("wall"); void load(); }} /> : <TrackSearch authenticated={Boolean(user)} onRequireAuth={() => onRequireAuth(`send-profile:${profile.handle}`)} onSelect={setSelectedTrack} actionLabel="Select" placeholder={`Find a song for ${profile.displayName}`} />}</section>}

            <nav className="profile-tabs" aria-label="Profile sections">
              {payload.featuredCount > 0 && <button className={tab === "featured" ? "active" : ""} onClick={() => { setTab("featured"); setSort("recent"); setFilter("all"); }}>Featured <b>{payload.featuredCount}</b></button>}
              <button className={tab === "library" ? "active" : ""} onClick={() => { setTab("library"); setSort("recent"); setFilter("all"); }}>Saved in Jamband <b>{payload.libraryCount}</b></button>
              <button className={tab === "wall" ? "active" : ""} onClick={() => { setTab("wall"); setSort("recent"); setFilter("all"); }}>Wall <b>{payload.wallCount}</b></button>
              {profile.isOwner && <button className={tab === "inbox" ? "active" : ""} onClick={() => { setTab("inbox"); setSort("recent"); setFilter("all"); }}>Private Inbox <b>{inbox.length}</b></button>}
            </nav>

            <section className="fragment-feed">
              <header className="feed-tools"><div><span className="eyebrow">{tab === "featured" ? "SPOTIFY-POWERED · OWNER CURATED" : tab === "wall" ? "VISITOR WALL" : tab === "library" ? "SAVED THROUGH JAMBAND" : "ONLY YOU CAN SEE THIS"}</span><h2>{tab === "featured" ? profile.libraryTitle : tab === "wall" ? "Songs left here" : tab === "library" ? "Saved in Jamband" : "Private recommendations"}</h2></div>{(tab === "wall" || tab === "library") && <div className="feed-selects"><label><span>Sort</span><select value={sort} onChange={(event) => setSort(event.target.value)}>{sortOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label><label><span>Filter</span><select value={filter} onChange={(event) => setFilter(event.target.value)}>{filterOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label></div>}</header>
              {tab === "featured" && <p className="featured-explainer">A ranked public rotation chosen from this profile’s Spotify collection. Playlist sources and ranking signals stay private.</p>}
              {fragments.length === 0 ? <div className="empty-fragments"><span>✧</span><h3>{tab === "wall" ? "No one has left a song yet" : tab === "library" ? "No Jamband saves yet" : tab === "featured" ? "Featured is waiting" : "Your private Inbox is clear"}</h3><p>{tab === "wall" ? "Be the first person to send an exact recommendation." : tab === "featured" ? "The profile owner can build a rotation from Spotify." : "Fragments saved from rooms and profiles land here."}</p></div> : <div className={`fragment-stack ${tab === "featured" ? "featured-stack" : ""}`}>{fragments.map((fragment) => <FragmentCard key={fragment.id} fragment={fragment} authenticated={Boolean(user)} currentHandle={user?.handle} onRequireAuth={onRequireAuth} ownerActions={profile.isOwner && (tab === "wall" || tab === "inbox")} onChanged={() => void load()} />)}</div>}
              {nextCursor && <div className="load-more-row"><button className="button ghost" disabled={loadingMore} onClick={() => void loadMore()}>{loadingMore ? "Loading songs…" : "Load 30 more"}</button></div>}
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}
